<template>
  <div class="booking-second-detail-step">
    <p class="booking-second-detail-step__intro">
      Answer the questions below. Required uploads appear after you select an option.
    </p>

    <div class="booking-second-detail-step__panel">
      <template v-for="(settings, i) in facilityAddonDetails" :key="'addon-' + i">
        <section
          v-if="
            (settings.title === 'Overlap' && enableOverlap) ||
            (settings.title === 'Non-Overlap' && enableNonOverlap)
          "
          class="booking-second-detail-step__item"
        >
          <h3 class="booking-second-detail-step__item-title">
            {{
              settings.title === "Overlap"
                ? "Overlapping add-ons"
                : "Additional facilities"
            }}
          </h3>

          <div v-if="settings.title === 'Overlap' && enableOverlap">
            <FormKit
              v-model="addonfacilitySection[i]"
              :options="setUpCheckBoxFacilityAddonSectionData(settings.data)"
              label="Select any that apply"
              type="checkbox"
            />
          </div>

          <div v-if="settings.title === 'Non-Overlap' && enableNonOverlap">
            <FormKit
              v-model="radiofacilitySection[i]"
              :options="setUpCheckBoxFacilityAddonSectionData(settings.data)"
              label="Select one option"
              type="radio"
            />
            <v-btn
              class="booking-second-detail-step__clear-btn"
              variant="text"
              @click="clearSelection(i)"
            >
              Clear selection
            </v-btn>
          </div>
        </section>
      </template>

      <section
        v-for="(settings, i) in detailsSettings"
        :key="'detail-' + i"
      >
        <div
          v-if="
            (settings.contentType === 'RADIO' ||
              settings.contentType === 'CHECKBOX') &&
            settings.isActive &&
            settings.venuetype === venueType
          "
          class="booking-second-detail-step__item"
        >
          <label
            class="booking-second-detail-step__label"
            v-html="parseTextWithLinks(settings.title)"
          ></label>
          <div
            class="booking-second-detail-step__choice-layout"
            :class="{
              'booking-second-detail-step__choice-layout--with-preview':
                selectedPreviewUrl(settings.content, detailsSelectedRadio[i]),
            }"
          >
            <div class="booking-second-detail-step__options-col">
              <div class="booking-second-detail-step__options">
                <label
                  v-for="option in settings.content"
                  :key="`${i}-${option.fieldText}`"
                  class="booking-second-detail-step__option"
                  :class="{
                    'booking-second-detail-step__option--selected': isDetailOptionSelected(
                      detailsSelectedRadio[i],
                      option.fieldText,
                      settings.contentType === 'CHECKBOX'
                    ),
                  }"
                >
                  <input
                    v-if="settings.contentType === 'RADIO'"
                    v-model="detailsSelectedRadio[i]"
                    type="radio"
                    :name="`detail-${i}-${settings.title}`"
                    :value="option.fieldText"
                    class="booking-second-detail-step__option-input"
                  />
                  <input
                    v-else
                    v-model="detailsSelectedRadio[i]"
                    type="checkbox"
                    :value="option.fieldText"
                    class="booking-second-detail-step__option-input"
                  />
                  <span class="booking-second-detail-step__option-text">
                    {{ option.fieldText }}
                  </span>
                </label>
              </div>
            </div>

            <div
              v-if="selectedPreviewUrl(settings.content, detailsSelectedRadio[i])"
              class="booking-second-detail-step__preview-col"
            >
              <img
                :src="selectedPreviewUrl(settings.content, detailsSelectedRadio[i])"
                class="booking-second-detail-step__preview-image"
                alt=""
              />
            </div>
          </div>

          <div
            v-if="docUploadRequired(settings.content, detailsSelectedRadio[i])"
            class="booking-second-detail-step__upload"
          >
            <div class="booking-second-detail-step__upload-row">
              <v-img
                v-if="imgValue[i]"
                :src="imgValue[i]"
                width="64"
                height="64"
                cover
                class="booking-second-detail-step__thumb"
              />
              <div class="booking-second-detail-step__upload-actions">
                <label class="booking-second-detail-step__file-btn">
                  Choose file
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, application/pdf"
                    @change="uploadSingleImage($event, i)"
                  />
                </label>
                <p class="booking-second-detail-step__required-note">
                  Document required
                </p>
                <div
                  v-if="detailImages[i]"
                  class="booking-second-detail-step__file-row"
                >
                  <v-icon
                    icon="mdi-check-circle"
                    :color="buttonColor"
                    size="16"
                  />
                  <p class="booking-second-detail-step__filename">
                    {{ detailImages[i].file.name }}
                  </p>
                </div>
                <div
                  v-if="errorImagetype"
                  class="booking-second-detail-step__error"
                >
                  Only PDF, JPG, PNG and JPEG files are allowed.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  setUpCheckBoxFacilityAddonSectionData,
  parseTextWithLinks,
  getImageUrlForSelection,
  isDocUploadRequiredForSelection,
  buildDetailQuestions,
  validateRequiredUploads,
  buildSecondPageDetails,
} from "@/utils/bookingSecondDetails";

export default {
  name: "BookingSecondDetailStep",
  props: {
    venueType: { type: String, default: "" },
    buttonColor: { type: String, default: "#0275FF" },
    detailsSettings: { type: Array, default: () => [] },
    facilityAddonDetails: { type: Array, default: () => [] },
    facilityAddonTemp: { type: Array, default: () => [] },
    enableOverlap: { type: Boolean, default: false },
    enableNonOverlap: { type: Boolean, default: false },
    storeaddedbookingdetails: { type: Array, default: () => [] },
  },
  data() {
    return {
      detailsSelectedRadio: [],
      detailImages: [],
      imgValue: [],
      errorImagetype: false,
      addonfacilitySection: [],
      radiofacilitySection: [],
    };
  },
  watch: {
    detailsSettings: {
      immediate: true,
      handler(settings) {
        (settings || []).forEach((setting, index) => {
          if (
            setting.contentType === "CHECKBOX" &&
            !Array.isArray(this.detailsSelectedRadio[index])
          ) {
            this.detailsSelectedRadio[index] = [];
          }
        });
      },
    },
  },
  methods: {
    setUpCheckBoxFacilityAddonSectionData,
    parseTextWithLinks(text) {
      return parseTextWithLinks(text, this.buttonColor);
    },
    docUploadRequired(content, selected) {
      return isDocUploadRequiredForSelection(content, selected);
    },
    isDetailOptionSelected(selected, fieldText, isCheckbox) {
      if (isCheckbox) {
        return Array.isArray(selected) && selected.includes(fieldText);
      }
      return selected === fieldText;
    },
    selectedPreviewUrl(content, selected) {
      return getImageUrlForSelection(content, selected);
    },
    clearSelection(i) {
      this.radiofacilitySection[i] = null;
    },
    uploadSingleImage(e, i) {
      const file = e.target.files[0];
      if (!file) return;

      const imageType = file.type;
      if (
        ["image/jpeg", "image/png", "image/jpg", "application/pdf"].includes(
          imageType
        )
      ) {
        this.errorImagetype = false;
        this.detailImages[i] = { file };
        if (imageType.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            this.imgValue[i] = ev.target.result;
          };
          reader.readAsDataURL(file);
        } else {
          this.imgValue[i] = null;
        }
      } else {
        this.errorImagetype = true;
      }
    },
    buildAddonPayloads() {
      let venueblockid;
      try {
        venueblockid = JSON.parse(sessionStorage.getItem("venueBlockId"));
      } catch {
        venueblockid = null;
      }
      const blockId = Array.isArray(venueblockid) ? venueblockid[0] : venueblockid;
      const booking = this.storeaddedbookingdetails[0] || {};

      const overlapSelections = (this.addonfacilitySection[0] || []).filter(
        (value) => value !== null
      );
      const overlapAddon = {
        VenueBlockId: blockId,
        StartDate: booking.StartTime,
        EndDate: booking.EndTime,
        VenueId: booking.venueId,
        AddonType: "Overlap",
        AddonName: overlapSelections,
      };

      const nonOverlapSelection = this.radiofacilitySection.find(
        (value) => value !== null && value !== undefined
      );
      const selectedRadioFacilities = nonOverlapSelection
        ? [nonOverlapSelection]
        : [];
      const nonOverlapAddon = {
        VenueBlockId: blockId,
        StartDate: booking.StartTime,
        EndDate: booking.EndTime,
        VenueId: booking.venueId,
        AddonType: "Non-Overlap",
        AddonName: selectedRadioFacilities,
      };

      let addonRateIncrease = 0;
      const isFree = sessionStorage.getItem("isFreeBooking") === "true";

      if (!isFree) {
        if (this.addonfacilitySection?.length > 0) {
          for (const facility of this.facilityAddonTemp) {
            if (facility.venuetype === this.venueType) {
              if (
                this.addonfacilitySection[0]?.includes(facility.facilityname) &&
                facility.addontype !== "Silent"
              ) {
                addonRateIncrease += Number(facility.facilitycost) || 0;
              }
            }
          }
        }
        if (this.radiofacilitySection.length > 0) {
          for (const facility of this.facilityAddonTemp) {
            if (facility.venuetype === this.venueType) {
              if (
                this.radiofacilitySection.includes(facility.facilityname) &&
                facility.addontype !== "Silent"
              ) {
                addonRateIncrease += Number(facility.facilitycost) || 0;
              }
            }
          }
        }
      }

      return { overlapAddon, nonOverlapAddon, addonRateIncrease };
    },
    validateAndBuild() {
      const { detailQuestions, error } = buildDetailQuestions(
        this.detailsSettings,
        this.detailsSelectedRadio,
        this.detailImages,
        this.venueType
      );

      if (error === "missing_answer") {
        this.$toast.error("Please fill all the required information.", {
          position: "top-right",
        });
        return null;
      }

      const missingUploads = validateRequiredUploads(detailQuestions);
      if (missingUploads.length > 0) {
        this.$toast.error("Please upload documents.", {
          position: "top-right",
          duration: 5000,
        });
        return null;
      }

      let venueBlockId;
      try {
        venueBlockId = JSON.parse(sessionStorage.getItem("venueBlockId"));
      } catch {
        this.$toast.error("Reservation expired. Please try again.", {
          position: "top-right",
        });
        return null;
      }

      const secondPageDetails = buildSecondPageDetails(
        detailQuestions,
        this.detailImages,
        venueBlockId
      );
      const { overlapAddon, nonOverlapAddon, addonRateIncrease } =
        this.buildAddonPayloads();

      return {
        secondPageDetails,
        detailQuestions,
        overlapAddon,
        nonOverlapAddon,
        addonRateIncrease,
      };
    },
  },
};
</script>

<style scoped src="./secondDetail.css"></style>
